import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Mic, MicOff, Loader2, Trash2, Sparkles, X, ArrowUpRight, Type, Undo2, Home, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function JobPhotoCapture({ jobId }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recogRef = useRef(null);
  const recorderRef = useRef(null);
  const canvasRef = useRef(null);
  const baseImg = useRef(null);
  const drag = useRef(null);

  const [mode, setMode] = useState("idle"); // idle | live | annotate
  const [note, setNote] = useState("");
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tool, setTool] = useState("arrow"); // arrow | label
  const [annos, setAnnos] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [wholeHome, setWholeHome] = useState(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;
  const fileUrl = (id) => `${BACKEND}/api/media/${id}/file?auth=${token}`;

  const loadPhotos = () => api.get(`/jobs/${jobId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  const loadQuotes = () => api.get(`/jobs/${jobId}/quotes`).then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { loadPhotos(); loadQuotes(); return () => stopCam(); /* eslint-disable-line */ }, [jobId]);

  // redraw annotate canvas
  useEffect(() => {
    if (mode !== "annotate" || !canvasRef.current || !baseImg.current) return;
    const c = canvasRef.current, ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(baseImg.current, 0, 0, c.width, c.height);
    ctx.lineWidth = Math.max(3, c.width / 200); ctx.strokeStyle = "#F59E0B"; ctx.fillStyle = "#F59E0B";
    ctx.font = `${Math.max(16, c.width / 45)}px sans-serif`;
    for (const a of annos) {
      if (a.type === "arrow") {
        ctx.beginPath(); ctx.moveTo(a.x1, a.y1); ctx.lineTo(a.x2, a.y2); ctx.stroke();
        const ang = Math.atan2(a.y2 - a.y1, a.x2 - a.x1), h = c.width / 40;
        ctx.beginPath(); ctx.moveTo(a.x2, a.y2);
        ctx.lineTo(a.x2 - h * Math.cos(ang - 0.4), a.y2 - h * Math.sin(ang - 0.4));
        ctx.lineTo(a.x2 - h * Math.cos(ang + 0.4), a.y2 - h * Math.sin(ang + 0.4));
        ctx.closePath(); ctx.fill();
      } else {
        ctx.fillStyle = "#000"; ctx.fillRect(a.x - 2, a.y - 18, ctx.measureText(a.text).width + 8, 24);
        ctx.fillStyle = "#F59E0B"; ctx.fillText(a.text, a.x + 2, a.y);
      }
    }
  }, [annos, mode, dims]);

  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = s;
      setMode("live");
      setTimeout(async () => { if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); } }, 50);
    } catch { toast.error("Camera permission denied or unavailable"); }
  };
  const stopCam = () => {
    stopDictation();
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setMode("idle");
  };

  const takeShot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) { toast.error("Camera not ready"); return; }
    stopDictation();
    const maxW = 1200, scale = Math.min(1, maxW / video.videoWidth);
    const w = Math.round(video.videoWidth * scale), h = Math.round(video.videoHeight * scale);
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    c.getContext("2d").drawImage(video, 0, 0, w, h);
    const img = new Image();
    img.onload = () => { baseImg.current = img; setAnnos([]); setDims({ w, h }); setMode("annotate");
      setTimeout(() => { if (canvasRef.current) { canvasRef.current.width = w; canvasRef.current.height = h; setAnnos((a) => [...a]); } }, 30); };
    img.src = c.toDataURL("image/jpeg", 0.85);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const evtXY = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvasRef.current.width / r.width), y: (e.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const onDown = (e) => {
    const p = evtXY(e);
    if (tool === "label") { const t = window.prompt("Label text (e.g. TV mount here)"); if (t) setAnnos((a) => [...a, { type: "label", x: p.x, y: p.y, text: t }]); return; }
    drag.current = p;
  };
  const onUp = (e) => { if (tool === "arrow" && drag.current) { const p = evtXY(e); setAnnos((a) => [...a, { type: "arrow", x1: drag.current.x, y1: drag.current.y, x2: p.x, y2: p.y }]); } drag.current = null; };

  const uploadShot = async () => {
    setBusy(true);
    const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/jpeg", 0.82));
    try {
      const fd = new FormData(); fd.append("file", blob, "capture.jpg"); fd.append("note", note.trim());
      await api.post(`/jobs/${jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(note.trim() ? "Uploaded · AI estimate drafted" : "Photo uploaded to cloud");
      setNote(""); setAnnos([]); baseImg.current = null; setMode("idle"); loadPhotos();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  // --- voice: Web Speech API with Whisper fallback ---
  const startDictation = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = "en-US";
      r.onresult = (e) => { let f = ""; for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) f += e.results[i][0].transcript + " "; if (f) setNote((n) => (n ? n + " " : "") + f.trim()); };
      r.onend = () => setListening(false); r.onerror = () => setListening(false);
      recogRef.current = r; r.start(); setListening(true); return;
    }
    // Fallback: record audio -> Whisper
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = []; const rec = new MediaRecorder(s);
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        s.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const fd = new FormData(); fd.append("file", blob, "note.webm");
        try { const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setNote((n) => (n ? n + " " : "") + data.text.trim()); toast.success("Transcribed"); }
        catch { toast.error("Transcription failed"); }
        setListening(false);
      };
      recorderRef.current = rec; rec.start(); setListening(true);
    } catch { toast.error("Microphone unavailable"); }
  };
  const stopDictation = () => {
    try { recogRef.current?.stop(); } catch { /* */ }
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* */ }
    recorderRef.current = null; setListening(false);
  };

  const del = async (mid) => { try { await api.delete(`/media/${mid}`); loadPhotos(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const reAnalyze = async (mid) => { setBusy(true); try { await api.post(`/media/${mid}/analyze`); toast.success("AI estimate refreshed"); loadPhotos(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } setBusy(false); };
  const runWholeHome = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/jobs/${jobId}/whole-home-estimate`, {}); setWholeHome(data.extracted); toast.success("Whole-home estimate drafted"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } setBusy(false);
  };
  const toQuote = async (source, id) => {
    try { const { data } = await api.post(`/jobs/${jobId}/quotes/from-estimate`, { source, id, tax_rate: 0 }); navigate(`/quotes/${data.id}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const EstimateBlock = ({ est, onQuote }) => (
    <div className="rounded-lg border border-purple-500/30 bg-purple-950/20 p-2 text-[11px] text-slate-300">
      <div className="mb-1 flex items-center gap-1 font-mono-tac uppercase text-purple-300"><Sparkles className="h-3 w-3" /> AI estimate draft</div>
      {est.summary && <p className="mb-1 text-slate-400">{est.summary}</p>}
      {(est.recommended_items || []).slice(0, 8).map((it, i) => (<div key={i} className="flex justify-between"><span>{it.item}</span><span className="text-slate-500">{it.qty} {it.trade ? `· ${it.trade}` : ""}</span></div>))}
      {typeof est.labor_hours === "number" && <p className="mt-1 text-slate-500">~{est.labor_hours} labor hrs</p>}
      {onQuote && <button data-testid="to-quote-btn" onClick={onQuote} className="mt-2 inline-flex items-center gap-1 rounded border border-amber-500/50 px-2 py-0.5 text-amber-300 hover:bg-amber-500/10"><FileText className="h-3 w-3" /> Convert to quote</button>}
    </div>
  );

  return (
    <div className="card-tac p-5 lg:col-span-2" data-testid="job-photos-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-cyan-400" /><h3 className="font-display font-semibold text-white">Site photos &amp; AI estimate</h3></div>
        {photos.length > 1 && <button data-testid="whole-home-btn" onClick={runWholeHome} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-cyan-600 px-3 py-1.5 text-xs text-cyan-300 disabled:opacity-50"><Home className="h-3.5 w-3.5" /> Whole-home estimate</button>}
      </div>

      {mode === "idle" && (
        <button data-testid="open-camera-btn" onClick={startCam} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Camera className="h-4 w-4" /> Open camera</button>
      )}

      {mode === "live" && (
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-black">
            <video ref={videoRef} playsInline muted className="max-h-[420px] w-full object-contain" data-testid="camera-video" />
            <button data-testid="close-camera-btn" onClick={stopCam} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white"><X className="h-4 w-4" /></button>
          </div>
          <button data-testid="shoot-btn" onClick={takeShot} className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm"><Camera className="h-4 w-4" /> Capture</button>
        </div>
      )}

      {mode === "annotate" && (
        <div className="space-y-3" data-testid="annotate-view">
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="tool-arrow" onClick={() => setTool("arrow")} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs ${tool === "arrow" ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`}><ArrowUpRight className="h-3.5 w-3.5" /> Arrow</button>
            <button data-testid="tool-label" onClick={() => setTool("label")} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs ${tool === "label" ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`}><Type className="h-3.5 w-3.5" /> Label</button>
            <button data-testid="undo-btn" onClick={() => setAnnos((a) => a.slice(0, -1))} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-slate-300"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
            <span className="font-mono-tac text-[10px] text-slate-500">{tool === "arrow" ? "drag to point at mount spots" : "tap to drop a label"}</span>
          </div>
          <canvas ref={canvasRef} onPointerDown={onDown} onPointerUp={onUp}
            style={{ touchAction: "none" }} className="w-full cursor-crosshair rounded-xl border border-slate-800 bg-black" />
          <div className="flex items-start gap-2">
            <textarea data-testid="photo-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder='Describe the scope, e.g. "mount a 75&quot; TV left of the fireplace, 7 in-ceiling speakers..."'
              className="flex-1 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none" />
            <button data-testid="dictate-btn" onClick={listening ? stopDictation : startDictation} className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs ${listening ? "border-rose-500 text-rose-300" : "border-slate-700 text-slate-300"}`}>{listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />} {listening ? "Stop" : "Dictate"}</button>
          </div>
          <div className="flex gap-2">
            <button data-testid="capture-btn" onClick={uploadShot} disabled={busy} className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {busy ? "Uploading…" : "Upload"}</button>
            <button onClick={() => { baseImg.current = null; setMode("idle"); }} className="rounded-full border border-slate-700 px-4 py-2.5 text-sm text-slate-300">Discard</button>
          </div>
        </div>
      )}

      {wholeHome && (
        <div className="mt-4 rounded-xl border border-cyan-600/40 bg-cyan-950/10 p-3" data-testid="whole-home-panel">
          <div className="mb-1 flex items-center justify-between"><span className="font-mono-tac text-[10px] uppercase text-cyan-300">Whole-home estimate</span><button onClick={() => setWholeHome(null)} className="text-slate-500"><X className="h-3.5 w-3.5" /></button></div>
          <EstimateBlock est={wholeHome} />
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="photo-gallery">
          {photos.map((m) => (
            <div key={m.id} data-testid={`photo-${m.id}`} className="overflow-hidden rounded-xl border border-slate-800 bg-[#0d1420]">
              <img src={fileUrl(m.id)} alt="site" className="h-40 w-full object-cover" />
              <div className="p-3">
                {m.note && <p className="mb-2 text-xs text-slate-300">“{m.note}”</p>}
                {m.ai_estimate && !m.ai_estimate._error && !m.ai_estimate._parse_error
                  ? <div data-testid={`estimate-${m.id}`}><EstimateBlock est={m.ai_estimate} onQuote={() => toQuote("media", m.id)} /></div>
                  : <button data-testid={`analyze-${m.id}`} onClick={() => reAnalyze(m.id)} className="inline-flex items-center gap-1 text-[11px] text-purple-300 hover:text-purple-200"><Sparkles className="h-3 w-3" /> Run AI estimate</button>}
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-mono-tac text-[10px] text-slate-600">{String(m.created_at).slice(0, 16).replace("T", " ")}</span>
                  <button onClick={() => del(m.id)} className="text-slate-600 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {quotes.length > 0 && (
        <div className="mt-5" data-testid="quotes-list">
          <div className="mb-2 font-mono-tac text-[10px] uppercase text-slate-500">Quotes</div>
          {quotes.map((q) => (
            <button key={q.id} data-testid={`quote-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)} className="mb-1.5 flex w-full items-center justify-between rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-2 text-left text-xs hover:border-amber-500/50">
              <span className="text-slate-300">{q.number} · <span className="text-slate-500">{q.status}</span></span>
              <span className="font-display font-bold text-white">${q.total?.toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
